import type {
  AuthSession,
  CreateInviteInput,
  CreateListInput,
  Invite,
  InvitePreview,
  Item,
  ListDetail,
  ListSummary,
  SigninInput,
  SignupInput,
  UpdateItemInput,
  UpdateListInput,
  User,
} from './types';

/** status 0 = network failure (offline, DNS, CORS, server down). */
export class ApiError extends Error {
  readonly status: number;
  readonly messages: string[];
  readonly body: unknown;

  constructor(status: number, messages: string[], body?: unknown) {
    super(messages.join('\n') || `Request failed (${status})`);
    this.name = 'ApiError';
    this.status = status;
    this.messages = messages.length ? messages : [this.message];
    this.body = body;
  }

  get isNetwork() {
    return this.status === 0;
  }
}

export function isApiError(e: unknown): e is ApiError {
  return e instanceof ApiError;
}

/** Extracts human readable messages from a Nest-style error body. */
export function parseErrorMessages(body: unknown, status: number, statusText = ''): string[] {
  if (body && typeof body === 'object' && 'message' in body) {
    const m = (body as { message: unknown }).message;
    if (Array.isArray(m)) {
      const list = m.filter((x): x is string => typeof x === 'string' && x.length > 0);
      if (list.length) return list;
    } else if (typeof m === 'string' && m) {
      return [m];
    }
  }
  if (typeof body === 'string' && body.trim() && body.length < 300 && !body.trim().startsWith('<')) {
    return [body.trim()];
  }
  if (status >= 500) return ['Something went wrong on the server. Please try again.'];
  return [statusText || `Request failed (${status})`];
}

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  body?: unknown;
  /** Send the bearer token (default true). */
  auth?: boolean;
  signal?: AbortSignal;
}

export interface ApiClientConfig {
  baseUrl: string;
  getToken: () => string | null;
  /** Called when an authenticated request gets a 401. */
  onUnauthorized?: () => void;
  fetch?: typeof fetch;
}

export class ApiClient {
  private config: ApiClientConfig;

  constructor(config: ApiClientConfig) {
    this.config = config;
  }

  configure(patch: Partial<ApiClientConfig>) {
    this.config = { ...this.config, ...patch };
  }

  async request<T>(path: string, opts: RequestOptions = {}): Promise<T> {
    const { method = 'GET', body, auth = true, signal } = opts;
    const headers: Record<string, string> = { Accept: 'application/json' };
    if (body !== undefined) headers['Content-Type'] = 'application/json';
    const token = auth ? this.config.getToken() : null;
    if (token) headers.Authorization = `Bearer ${token}`;

    const doFetch = this.config.fetch ?? globalThis.fetch.bind(globalThis);
    let res: Response;
    try {
      res = await doFetch(this.config.baseUrl.replace(/\/$/, '') + path, {
        method,
        headers,
        body: body === undefined ? undefined : JSON.stringify(body),
        signal,
      });
    } catch (err) {
      if ((err as { name?: string })?.name === 'AbortError') throw err;
      throw new ApiError(0, ["Can't reach Cady. Check your connection and try again."]);
    }

    // 204 / empty body tolerant parsing
    let parsed: unknown = undefined;
    if (res.status !== 204 && res.status !== 205) {
      const text = await res.text().catch(() => '');
      if (text) {
        try {
          parsed = JSON.parse(text);
        } catch {
          parsed = text;
        }
      }
    }

    if (!res.ok) {
      if (res.status === 401 && token) this.config.onUnauthorized?.();
      throw new ApiError(res.status, parseErrorMessages(parsed, res.status, res.statusText), parsed);
    }
    return parsed as T;
  }

  // ---- auth
  signup(input: SignupInput) {
    return this.request<AuthSession>('/auth/signup', { method: 'POST', body: input, auth: false });
  }
  signin(input: SigninInput) {
    return this.request<AuthSession>('/auth/signin', { method: 'POST', body: input, auth: false });
  }
  me() {
    return this.request<User>('/auth/me');
  }
  signout() {
    return this.request<{ ok: true }>('/auth/signout', { method: 'POST' });
  }

  // ---- lists
  lists() {
    return this.request<ListSummary[]>('/lists');
  }
  createList(input: CreateListInput) {
    return this.request<ListSummary>('/lists', { method: 'POST', body: input });
  }
  list(id: string) {
    return this.request<ListDetail>(`/lists/${enc(id)}`);
  }
  updateList(id: string, input: UpdateListInput) {
    return this.request<ListSummary>(`/lists/${enc(id)}`, { method: 'PATCH', body: input });
  }
  deleteList(id: string) {
    return this.request<void>(`/lists/${enc(id)}`, { method: 'DELETE' });
  }

  // ---- items
  addItem(listId: string, text: string) {
    return this.request<Item>(`/lists/${enc(listId)}/items`, { method: 'POST', body: { text } });
  }
  updateItem(listId: string, itemId: string, input: UpdateItemInput) {
    return this.request<Item>(`/lists/${enc(listId)}/items/${enc(itemId)}`, { method: 'PATCH', body: input });
  }
  deleteItem(listId: string, itemId: string) {
    return this.request<void>(`/lists/${enc(listId)}/items/${enc(itemId)}`, { method: 'DELETE' });
  }

  // ---- members
  removeMember(listId: string, userId: string) {
    return this.request<void>(`/lists/${enc(listId)}/members/${enc(userId)}`, { method: 'DELETE' });
  }

  // ---- invites
  createInvite(listId: string, input: CreateInviteInput = {}) {
    return this.request<Invite>(`/lists/${enc(listId)}/invites`, { method: 'POST', body: input });
  }
  invites(listId: string) {
    return this.request<Invite[]>(`/lists/${enc(listId)}/invites`);
  }
  revokeInvite(listId: string, inviteId: string) {
    return this.request<void>(`/lists/${enc(listId)}/invites/${enc(inviteId)}`, { method: 'DELETE' });
  }
  invitePreview(token: string) {
    return this.request<InvitePreview>(`/invites/${enc(token)}`);
  }
  acceptInvite(token: string) {
    return this.request<ListSummary>(`/invites/${enc(token)}/accept`, { method: 'POST' });
  }
}

function enc(s: string) {
  return encodeURIComponent(s);
}

export const TOKEN_KEY = 'cady_token';

export function readToken(): string | null {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}

export const API_BASE: string = import.meta.env.VITE_API_URL ?? '/api';

/** App-wide client. AuthProvider wires onUnauthorized. */
export const api = new ApiClient({ baseUrl: API_BASE, getToken: readToken });
